import { Injectable } from "@angular/core";
import {
  AvcorePublishStreamApiCredentials,
  StreamId,
  StreamKind,
  StreamSpecs
} from "@common/domain";
import { ErrorWithCause } from "@common/error-with-cause";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { ErrorHandlerService } from "@youm-front/utils/error-handler";
import {
  catchError,
  concatMap,
  defer,
  groupBy,
  map,
  merge,
  mergeMap,
  timeout,
  TimeoutError
} from "rxjs";
import { LocalAnyConferenceStream } from "youm-lib/conference";
import { LocalConferenceStreamActions } from "../actions";
import { LocalConferenceStreamsListService } from "../service";

@Injectable()
export class LocalConferenceStreamEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly localConferenceStreamsListService: LocalConferenceStreamsListService,
    private readonly errorHandlerService: ErrorHandlerService
  ) {}

  // Streams whose publish timed out: if the publish completes later,
  // the stream is destroyed instead of being added (no orphaned producer).
  private readonly _abandoned = new WeakSet<LocalAnyConferenceStream>();

  // Avoid RC on a stream,
  // sequentially process events for the same stream.
  seqStreamHandler$ = createEffect(() =>
    merge(
      LocalConferenceStreamActions.streamAdd.actions$.pipe(
        map((_) => ({
          streamId: _.stream.streamId,
          action: _
        }))
      ),
      this.actions$.pipe(
        ofType(LocalConferenceStreamActions.streamDelete),
        map((_) => ({
          streamId: _.id,
          action: _
        }))
      )
    ).pipe(
      groupBy((_) => _.streamId),
      mergeMap((_) =>
        _.pipe(
          concatMap(({ action }) =>
            defer(() => this.handler(action)).pipe(
              timeout(15_000),
              mergeMap(() => []),
              catchError((error) => {
                this.errorHandlerService.error(error);

                // Report a failed publish, so that its 'Room Stream'
                // is not left without media.
                return action.type ===
                  LocalConferenceStreamActions.streamAdd.type
                  ? this.streamAddFailure(action.stream, error)
                  : [];
              })
            )
          )
        )
      )
    )
  );

  dispose$ = createEffect(() =>
    this.actions$.pipe(ofType(LocalConferenceStreamActions.dispose)).pipe(
      map(() => this.localConferenceStreamsListService.streams),
      mergeMap((_) =>
        _.map((_) =>
          LocalConferenceStreamActions.streamDelete({ id: _.streamId })
        )
      )
    )
  );

  private async handler(
    action:
      | ReturnType<typeof LocalConferenceStreamActions.streamAdd>
      | ReturnType<typeof LocalConferenceStreamActions.streamDelete>
  ) {
    switch (action.type) {
      case LocalConferenceStreamActions.streamAdd.type:
        return this.streamAdd(action.stream, action.kinds, action.credentials);
      case LocalConferenceStreamActions.streamDelete.type:
        return this.streamDelete(action.id);
      default:
        return;
    }
  }

  private async streamAdd<T extends LocalAnyConferenceStream>(
    stream: T,
    kinds: StreamKind[],
    credentials: AvcorePublishStreamApiCredentials
  ) {
    const { streamId } = stream;

    const prevStream = this.localConferenceStreamsListService.streams.find(
      (_) => _.streamId === streamId
    );
    if (prevStream) {
      await prevStream.destroy();
      this.localConferenceStreamsListService.remove(prevStream);
    }

    // Timed out (and destroyed) while the previous stream was destroyed.
    if (this._abandoned.has(stream)) {
      return;
    }

    try {
      await stream.publish(
        {
          streamId,
          kinds
        },
        credentials
      );
      if (this._abandoned.has(stream)) {
        await stream.destroy();
        return;
      }
      this.localConferenceStreamsListService.add(stream);
    } catch (error) {
      // Already destroyed when its publish timed out.
      if (this._abandoned.has(stream)) {
        throw error;
      }
      await stream.destroy().catch((_) => {
        throw new ErrorWithCause(_.message, error, _);
      });
      this.localConferenceStreamsListService.remove(stream);

      throw error;
    }
  }

  private async streamAddFailure(
    stream: LocalAnyConferenceStream,
    error: Error
  ) {
    // A timed out screen publish is still running: stop it (and its
    // capture), its late connection must not survive (avcore clears the
    // pending socket). camMic and file streams keep their behaviour.
    if (
      error instanceof TimeoutError &&
      StreamSpecs.isOfType(stream.streamId, "screen")
    ) {
      this._abandoned.add(stream);
      this.localConferenceStreamsListService.remove(stream);
      await stream.destroy().catch((_) => this.errorHandlerService.error(_));
    }

    return LocalConferenceStreamActions.streamAddFailure({
      id: stream.streamId,
      error: error.message
    });
  }

  private async streamDelete(id: StreamId) {
    const stream = this.localConferenceStreamsListService.streams.find(
      (stream) => stream.streamId === id
    );
    if (!stream) {
      return;
    }

    this.localConferenceStreamsListService.set([
      ...this.localConferenceStreamsListService.streams.filter(
        (_) => _.streamId !== id
      )
    ]);

    // Destroy stream after it was deleted from the streams list,
    // avoid accessing the destroyed stream.
    await stream.destroy();
  }
}
